import { type FC, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import ModalLayout from 'components/modals/baseModal';

import { getCurrency } from 'use-cases/getPriceAndCurrency';

import { sendAnalyticEvent } from 'data/actions/analytics';
import { toggleModal } from 'data/actions/modals';

import { isMobile } from 'utils/isMobile';

import { PAGE_LINKS } from 'ts/constants/page-links';
import { EModalTypes } from 'ts/enums/modal.types';

import arrowDownIcon from './assets/arrowDownIcon.svg';
import { ReactComponent as Cross } from './assets/cross.svg';
import { ReactComponent as RightIcon } from './assets/rightIcon.svg';
import CreditsBlock from './components/CreditsBlock';
import DetailsBlock from './components/DetailsBlock';
import { useOneTimeUpsaleProducts } from './helpers/useOneTimeUpsaleProducts';

const UpsaleMainModal: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { upsalePlans, isLoading, selectedPlan, setSelectedPlan, handleBuyUpsaleProduct, isBuying, currentPlace } =
    useOneTimeUpsaleProducts();
  const [showCloseButton, setShowCloseButton] = useState(false);
  const [planWithDetails, setPlanWithDetails] = useState<string | null>(null);
  const [showMoreOptions, setShowMoreOptions] = useState(false);

  const handleCloseModal = () => {
    dispatch(
      sendAnalyticEvent({
        event: 'upsale_modal_close_tap',
        data: {
          screen_config_name: 'transactional_credits_access',
          placement: currentPlace()
        }
      })
    );
    dispatch(toggleModal({ visible: true, type: EModalTypes.UPSALE_SUCCESS, options: { skip: true } }));
  };

  const handlePlanClick = (item: any) => {
    setSelectedPlan(item);
    setPlanWithDetails(item.id);
  };

  useEffect(() => {
    dispatch(
      sendAnalyticEvent({
        event: 'upsale_modal_view',
        data: {
          screen_config_name: 'transactional_credits_access',
          placement: currentPlace()
        }
      })
    );
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (window.location.pathname.includes(PAGE_LINKS.DASHBOARD)) {
      setShowCloseButton(true);
    }
    const timer = setTimeout(() => {
      setShowCloseButton(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (upsalePlans.length > 0 && !planWithDetails) {
      setPlanWithDetails(upsalePlans[0].id);
    }
  }, [upsalePlans, planWithDetails]);

  const onSeeMoreOptionsClick = () => {
    setShowMoreOptions(true);
    dispatch(
      sendAnalyticEvent({
        event: 'upsale_modal_see_more_options',
        data: {
          screen_config_name: 'transactional_credits_access',
          placement: currentPlace()
        }
      })
    );
  };

  const onAcceptClick = () => {
    handleBuyUpsaleProduct();
    dispatch(
      sendAnalyticEvent({
        event: 'upsale_modal_accept_tap',
        data: {
          screen_config_name: 'transactional_credits_access_expandable',
          placement: currentPlace(),
          planName: selectedPlan?.name
        }
      })
    );
  };

  return (
    <ModalLayout fullSizeModal={isMobile}>
      <div className='bg-white tablet:rounded-xl tablet:max-w-[650px] flex h-full tablet:h-auto overflow-y-auto tablet:overflow-visible'>
        <div className='flex flex-col tablet:w-full pt-10 tablet:p-12 tablet:gap-8 relative min-h-0 flex-1'>
          <div className='tablet:hidden'>
            {showCloseButton && (
              <button
                onClick={handleCloseModal}
                className='absolute top-[12px] right-[12px] z-10'
              >
                <Cross className='w-4 h-4' />
              </button>
            )}
          </div>
          <div className='tablet:flex tablet:justify-between tablet:items-start'>
            <div className='flex flex-col gap-2 px-4 tablet:px-0 tablet:flex-1'>
              <h1 className='text-[#000000de] text-[24px] leading-[30px] font-[800] tablet:text-[32px] tablet:font-extrabold tablet:leading-9 text-center tablet:text-left'>
                {t('upsale_modal.title')}
              </h1>
              <p className='text-[#00000099] text-[14px] leading-[18px] tablet:text-[16px] tablet:leading-[22px] text-center tablet:text-left'>
                {t('upsale_modal.description')}
              </p>
            </div>
            <div className='hidden tablet:block'>
              {showCloseButton && (
                <button
                  onClick={handleCloseModal}
                  className='absolute top-5 right-5'
                >
                  <Cross />
                </button>
              )}
            </div>
          </div>
          <div className='flex flex-col mt-3 tablet:mt-0 gap-2 px-4 tablet:px-0'>
            {isLoading ? (
              <>
                {[1, 2, 3].map((index) => (
                  <div
                    key={index}
                    className='relative tablet:py-5 tablet:px-6 border tablet:rounded-xl tablet:border-[rgba(0, 0, 0, 0.15)] flex justify-between animate-pulse'
                  >
                    <div className='flex gap-2 items-center'>
                      <div className='w-5 h-5 bg-gray-200 rounded-full' />
                      <div className='w-20 h-6 bg-gray-200 rounded' />
                    </div>
                    <div className='w-16 h-6 bg-gray-200 rounded' />
                  </div>
                ))}
              </>
            ) : (
              <>
                {upsalePlans.slice(0, 1).map((item) => (
                  <CreditsBlock
                    key={item.credits}
                    item={item}
                    selectedPlan={selectedPlan}
                    planWithDetails={planWithDetails}
                    onPlanClick={handlePlanClick}
                  >
                    <DetailsBlock />
                  </CreditsBlock>
                ))}

                {!showMoreOptions && (
                  <button
                    onClick={onSeeMoreOptionsClick}
                    className='text-[#5F30E2] text-[14px] font-medium text-center py-2 hover:underline flex items-center justify-center gap-1'
                  >
                    {t('upsale_modal.see_more_options')}
                    <img
                      src={arrowDownIcon}
                      alt='arrowDownIcon'
                    />
                  </button>
                )}
                {showMoreOptions &&
                  upsalePlans.slice(1).map((item) => (
                    <CreditsBlock
                      key={item.credits}
                      item={item}
                      selectedPlan={selectedPlan}
                      planWithDetails={planWithDetails}
                      onPlanClick={handlePlanClick}
                      isBestValue={item?.id === upsalePlans[2]?.id}
                    >
                      <DetailsBlock />
                    </CreditsBlock>
                  ))}
              </>
            )}
          </div>

          <div className='sticky min-w-[375px] bg-white bottom-0 left-0 right-0 tablet:relative px-4 py-2 tablet:p-0 shadow-[0px_-4px_4px_0px_rgba(0,0,0,0.05)]'>
            <button
              disabled={isBuying}
              className='flex justify-center items-center gap-2 py-4 px-6 bg-[#5F30E2] rounded-2xl text-white w-full text-[18px] leading-6 font-bold'
              onClick={onAcceptClick}
            >
              {t('upsale_modal.unlock_more_credits')} <RightIcon />
            </button>
            <div className='flex justify-center items-center mt-2'>
              <p className='mt-2 text-[14px] text-[#616161] text-center'>
                {t('upsale_modal.pay_description', {
                  price: `${getCurrency(selectedPlan?.price?.currency || '')} ${
                    selectedPlan?.price?.price && selectedPlan?.price?.price / 100
                  }`
                })}
              </p>
            </div>
          </div>
        </div>
      </div>
    </ModalLayout>
  );
};

export default UpsaleMainModal;
